import type { StaffCatalogItemDetail } from "@lafayette/schemas";
import { useState } from "react";

import type { CatalogCapabilities } from "../allowed-actions";
import type { SaveMessage, useCatalogCommand } from "../commands";
import { useReportDirty } from "../unsaved";
import { useDraft } from "../use-draft";
import type { FieldErrors } from "../validation";

/** What every section of the edit page receives. */
export type ItemSectionContext = Readonly<{
  detail: StaffCatalogItemDetail;
  /** No edit capability, or the item is archived: show the values, offer no save. */
  readOnly: boolean;
  capabilities: CatalogCapabilities;
  /** One command runner for the whole page: it keeps saves from running at the same time. */
  command: ReturnType<typeof useCatalogCommand>;
  now: Date;
}>;

/**
 * The state every independently saved section has: a draft against the server's values, the field
 * errors of the last failed check, a counter that moves focus to the first invalid field, and the
 * message of the last save.
 */
export function useSectionState<T>(dirtyKey: string, server: T) {
  const draft = useDraft(server);
  useReportDirty(dirtyKey, draft.dirty);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [revision, setRevision] = useState(0);
  const [message, setMessage] = useState<SaveMessage | null>(null);

  /** A check failed: show the errors, move focus to the first invalid field, clear the old message. */
  function reject(next: FieldErrors) {
    setErrors(next);
    setRevision((current) => current + 1);
    setMessage(null);
  }

  return {
    ...draft,
    errors,
    clearErrors: () => setErrors({}),
    revision,
    reject,
    message,
    setMessage,
  };
}

/** The fields with an error, in page order, as the error summary lists them. */
export function errorFieldsOf(
  errors: FieldErrors,
  fields: readonly Readonly<{ key: string; id: string; label: string }>[],
): readonly Readonly<{ id: string; label: string }>[] {
  return fields.filter((field) => errors[field.key] !== undefined);
}
